import * as React from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  isPushAvailable,
  onPushTapped,
  onPushReceived,
  getInitialPushNotification,
} from '@3pm-source/native-connector';
import { FEED_KEY } from './use-notifications';
import { markRead, type NotificationKind } from './api';
import { routeFor } from './notification-routing';
import { invalidateJobData } from './job-cache';
import { initPushRegistration } from './push';

/**
 * Native tap routing. Mounted inside the authenticated app, so by the time this
 * runs the driver is past the auth gate — safe to consume the launch tap and
 * navigate. No-op on web.
 */
export function usePushRouting() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Resolve push status on every authed mount (login triggers registration too,
  // but a reload with a persisted session does not). On web this just marks push
  // 'inactive' so the web toast turns on; on native it registers + reports status.
  React.useEffect(() => {
    void initPushRegistration();
  }, []);

  React.useEffect(() => {
    if (!isPushAvailable()) return;

    // Acting on a notification (OS tap or launch) marks the matching feed row
    // read, then navigates: the job detail for assigned/rescheduled/updated,
    // the dispatch list for cancelled/removed (no job left to open).
    const handleTap = (msg: { data?: Record<string, string> } | null) => {
      const data = msg?.data;
      if (!data) return;
      if (data.notificationId) {
        void markRead(data.notificationId).catch(() => {});
        qc.invalidateQueries({ queryKey: FEED_KEY });
      }
      // The tapped notification exists because the job changed — drop the
      // cached copy so the detail page we're about to land on refetches
      // instead of serving the pre-update snapshot (staleTime would
      // otherwise keep it for up to 30s, and the poll for up to 60s).
      invalidateJobData(qc);
      navigate(routeFor(data.kind as NotificationKind, data.jobId));
    };

    // Cold-start: a tap that launched the app (consumed exactly once by the shell).
    void getInitialPushNotification().then(handleTap);

    const offTapped = onPushTapped(handleTap);

    const offReceived = onPushReceived(() => {
      // Option A: the shell posts a foreground OS banner, so we do NOT show an
      // in-app toast here (that would double up). We only refresh the badge/feed
      // so the bell count is current; the banner itself is the popup, and its
      // tap routes via onPushTapped above.
      qc.invalidateQueries({ queryKey: FEED_KEY });
      // The push is our real-time signal that the job changed. Invalidate the
      // job caches so a detail/list screen the driver is looking at right now
      // refreshes immediately rather than waiting out its 60s poll.
      invalidateJobData(qc);
    });

    return () => {
      offTapped();
      offReceived();
    };
  }, [navigate, qc]);
}
